import {CSSProperties} from "react";
import survivorImg from "../../../../assets/stats-survivor.png";

// Персонаж DayZ (вирізаний зі скріншота, removebg) з точками влучань на тілі.
// Персонаж стоїть обличчям до глядача, тож його ліва рука — праворуч на картинці.

export type ZoneId = "head" | "torso" | "leftarm" | "rightarm" | "lefthand" | "righthand" | "leftleg" | "rightleg" | "leftfoot" | "rightfoot";

// Розмір картинки й точки частин тіла в частках її ширини/висоти. Інша картинка — інші числа тут.
const FIGURE = {
    src: survivorImg,
    w: 192,
    h: 634,
    anchors: {
        head: [0.44, 0.18],
        torso: [0.47, 0.38],
        rightarm: [0.1, 0.43],
        righthand: [0.06, 0.55],
        leftarm: [0.87, 0.4],
        lefthand: [0.9, 0.55],
        rightleg: [0.34, 0.68],
        leftleg: [0.62, 0.68],
        rightfoot: [0.29, 0.9],
        leftfoot: [0.74, 0.95],
    } as Record<ZoneId, [number, number]>,
};

// права половина тіла персонажа підписується зліва, ліва — справа
const LEFT_SIDE: ZoneId[] = ["head", "righthand", "rightarm", "rightleg", "rightfoot"];

const VIEW_W = 400;
const VIEW_H = 580;
const IMG_H = 560;
const IMG_W = IMG_H * FIGURE.w / FIGURE.h;
const IMG_X = (VIEW_W - IMG_W) / 2;
const IMG_Y = 10;
const LABEL_GAP = 40; // мінімальна відстань між підписами по висоті

export interface SurvivorZone { id: ZoneId, label: string, short: string, count: number, share: number }

interface Props {
    zones: SurvivorZone[],
    active: ZoneId | null,
    onActive: (id: ZoneId | null) => void,
}

// Підписи однієї сторони розставляємо зверху вниз біля своєї точки, але не ближче LABEL_GAP один до одного
const layoutSide = (items: { id: ZoneId, y: number }[]) => {
    const sorted = [...items].sort((a, b) => a.y - b.y);
    let prev = -Infinity;
    return Object.fromEntries(sorted.map((it) => {
        const ly = Math.min(VIEW_H - 18, Math.max(prev + LABEL_GAP, it.y, 16));
        prev = ly;
        return [it.id, ly];
    })) as Record<ZoneId, number>;
};

const Survivor = ({ zones, active, onActive }: Props) => {
    const max = Math.max(1, ...zones.map((z) => z.count));
    const points = zones.map((z) => {
        const [fx, fy] = FIGURE.anchors[z.id];
        return { ...z, x: IMG_X + fx * IMG_W, y: IMG_Y + fy * IMG_H, left: LEFT_SIDE.includes(z.id) };
    });
    const labelY = {
        ...layoutSide(points.filter((p) => p.left)),
        ...layoutSide(points.filter((p) => !p.left)),
    };

    return (
        <svg className="sv" viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} role="img" aria-label="Персонаж із влучаннями по частинах тіла">
            <image className="sv-figure" href={FIGURE.src} x={IMG_X} y={IMG_Y} width={IMG_W} height={IMG_H} />

            { points.map((p) => {
                const ly = labelY[p.id];
                const lx = p.left ? 90 : VIEW_W - 90;
                const ex = p.left ? lx + 14 : lx - 14;
                // корінь вирівнює шкалу: інакше тулуб із сотнями влучань гасить решту тіла
                const heat = p.count ? Math.sqrt(p.count / max) : 0;
                return (
                    <g
                        key={p.id}
                        className={`sv-zone ${active === p.id ? "on" : ""} ${p.count ? "" : "zero"}`}
                        style={{ "--heat": heat } as CSSProperties}
                        onMouseEnter={() => onActive(p.id)}
                        onMouseLeave={() => onActive(null)}
                    >
                        <title>{`${p.label}: ${p.count}`}</title>
                        <polyline points={`${p.x},${p.y} ${ex},${ly} ${lx},${ly}`} />
                        { p.count > 0 && <circle className="sv-pulse" cx={p.x} cy={p.y} r={6 + heat * 10} /> }
                        <circle className="sv-dot" cx={p.x} cy={p.y} r={p.count ? 4 + heat * 4 : 3} />
                        <text x={p.left ? lx - 6 : lx + 6} y={ly + 1} textAnchor={p.left ? "end" : "start"}>
                            <tspan className="sv-pct">{p.count ? `${Math.round(p.share * 100)}%` : "—"}</tspan>
                            <tspan className="sv-name" x={p.left ? lx - 6 : lx + 6} dy="14">{p.short}</tspan>
                        </text>
                    </g>
                );
            })}
        </svg>
    );
};

export default Survivor;
